import { useEffect } from "react";
import {
  checkIsDueDateBeforeOpenDate,
  checkIsCloseDateBeforeDueDate,
  checkIsCloseDateBeforeOpenDate,
} from "ClassRoom/components/AddAssignment/AssignmentSettings/Utils";

/**
 * Custom hook to validate assignment dates (open, due, close) and update error messages
 *
 * This hook checks for date validation errors:
 * - Due date before open date
 * - Close date before due date
 * - Close date before open date
 * - User access validation
 *
 * @param {Object} params - Hook parameters
 * @param {string} params.dueDate - Assignment due date (ISO string)
 * @param {string} params.closeSubmissionDate - Submission close date (ISO string)
 * @param {string} params.publishedAt - Assignment publish/open date (ISO string)
 * @param {Function} params.setFooterDisabled - Callback to disable/enable footer
 * @param {Object} params.errorMsgLocalesRef - Ref object to store error message locale key
 * @param {boolean} params.isNotUserCourse - Flag indicating if user doesn't have access to course
 */
const useAssignmentDateValidation = ({
  dueDate,
  closeSubmissionDate,
  publishedAt,
  setFooterDisabled,
  errorMsgLocalesRef,
  isNotUserCourse,
}) => {
  useEffect(() => {
    if (!dueDate && !closeSubmissionDate && !publishedAt) {
      return;
    }

    let hasValidationError = false;

    if (
      checkIsDueDateBeforeOpenDate({ dueOn: dueDate, opensOn: publishedAt }) &&
      dueDate
    ) {
      errorMsgLocalesRef.current = "calendar:due_date_before_open_date_error";
      hasValidationError = true;
    } else if (
      checkIsCloseDateBeforeDueDate({
        closesOn: closeSubmissionDate,
        dueOn: dueDate,
      }) &&
      closeSubmissionDate &&
      dueDate
    ) {
      errorMsgLocalesRef.current = "calendar:close_date_before_due_date_error";
      hasValidationError = true;
    } else if (
      checkIsCloseDateBeforeOpenDate({
        closesOn: closeSubmissionDate,
        opensOn: publishedAt,
      }) &&
      closeSubmissionDate
    ) {
      errorMsgLocalesRef.current = "calendar:close_date_before_open_date_error";
      hasValidationError = true;
    } else if (isNotUserCourse) {
      errorMsgLocalesRef.current =
        "classRoom:cannot_access_class_task_sentence_case";
    } else {
      errorMsgLocalesRef.current = null;
    }

    setFooterDisabled(hasValidationError);
  }, [
    dueDate,
    closeSubmissionDate,
    publishedAt,
    setFooterDisabled,
    errorMsgLocalesRef,
    isNotUserCourse,
  ]);
};

export default useAssignmentDateValidation;
